import { Base } from '@studiometa/js-toolkit';
import { historyPush, nextMicrotask } from '@studiometa/js-toolkit/utils';
import { Panel } from '@studiometa/ui';
import ListingCheckbox from './ListingChekbox.js';
import ListingSelect from './ListingSelect.js';

/**
 * @typedef {{
 * $el: HTMLElement;
 * $refs: {
 *  list: HTMLElement;
 *  form: HTMLFormElement;
 *  loader: HTMLElement;
 *  panel: HTMLElement;
 *  dates: HTMLElement;
 *  relatedEvent: HTMLElement
 *  open: HTMLButtonElement[];
 *  close: HTMLButtonElement[];
 *};
 * $options: {
 *  url: string;
 * };
 * $children: {
 *  ListingCheckbox: ListingCheckbox[],
 *  ListingSelect: ListingSelect[],
 *  Panel: Panel[],
 * };
 * }} ListingProps
 */

/**
 * @augments {Base<ListingProps>}
 */
export default class Listing extends Base {
  /**
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'Listing',
    refs: [
      'list',
      'form',
      'loader',
      'open[]',
      'panel',
      'close[]',
      'reset',
      'displayDates[]',
      'hideDates[]',
      'dates',
      'relatedEvent',
    ],
    components: {
      ListingCheckbox,
      ListingSelect,
      Panel,
    },
    options: {
      url: {
        type: String,
        default: '/wp/wp-admin/admin-ajax.php',
      },
    },
  };

  /**
   * on mounted get post
   */
  mounted() {
    this.getPosts();
    this.currentUrl = window.location.href;
    if (
      this.currentUrl.includes('/agenda/?movie_section=hors-festival') ||
      this.currentUrl.includes('/agenda/?movie_section=off-festival')
    ) {
      this.$refs.dates.classList.toggle('hidden');
    }
  }

  /**
   * display dates
   */
  onDisplayDatesClick() {
    this.showDates();
  }

  /**
   * display dates
   */
  showDates() {
    this.$refs.dates.classList.remove('hidden');
  }

  /**
   * hide dates
   */
  hideDates() {
    this.$refs.dates.classList.add('hidden');
  }

  /**
   * hide dates
   */
  onHideDatesClick() {
    this.hideDates();
  }

  /**
   * Get posts
   */
  getPosts() {
    // Get current form params
    const data = new FormData(this.$refs.form);

    this.$refs.list.style.opacity = '0';
    this.$refs.loader.style.opacity = '1';

    // @ts-ignore @todo find a way to have good type
    const search = new URLSearchParams(data);
    data.append('action', 'listing');

    // fetch
    fetch(this.$options.url, {
      method: 'POST',
      credentials: 'same-origin',
      body: data,
    })
      .then((response) => {
        return response.json();
      })
      .then((responseHTML) => {
        if (responseHTML) {
          this.$refs.list.innerHTML = responseHTML.listing;

          if (this.$refs.relatedEvent) {
            this.$refs.relatedEvent.innerHTML = responseHTML.related_events;
          }

          // Do not show post_type in URL
          search.delete('post_type');
          historyPush({ search });

          this.$refs.list.style.opacity = '1';
          this.$refs.loader.style.opacity = '0';

          nextMicrotask(() => {
            this.$root.$update();
          });
        }
      })

      .catch((error) => {
        console.log('[Listing component]');

        console.error(error);
      });
  }

  /**
   *
   * @param {Event} event
   */
  onFormSubmit(event) {
    // prevent submit
    event.preventDefault();
    this.getPosts();
  }

  /**
   * submit fn
   */
  submit() {
    const evt = new Event('submit', { bubbles: true, cancelable: true });
    this.$refs.form.dispatchEvent(evt);
  }

  /**
   * onListingCheckboxChange
   */
  onListingCheckboxClick() {
    this.submit();
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome
  }

  /**
   * onListingSelectChange
   */
  onListingSelectChange() {
    this.submit();
  }

  /**
   * onListingSelectChange
   */
  onResetClick() {
    setTimeout(() => {
      this.submit();
    }, 100);
  }

  /**
   * onOpen
   */
  onOpenClick() {
    this.open();
  }

  /**
   * open fn
   */
  open() {
    this.$refs.panel.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  /**
   * onClose
   */
  onCloseClick() {
    this.close();
  }

  /**
   * close fn
   */
  close() {
    this.$refs.panel.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }
}
